import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Table, Pagination, Skeleton } from "antd";

import AllResignationAddPopup from "components/modelpopup/AllResignationAddPopup";
import ResignationFilterDrawer from "components/Drawer/resignationsFilterDrawer";
import PageHeader from "components/PageHeader";
import ListShell from "components/ListShell";
import { Button } from "components/Button";
import { itemRender, pageSizeOptions } from "utils/table";
import useStickyIdentity from "views/pages/UniformManagement/components/useStickyIdentity";

import ResignationListFilter from "../components/ResignationListFilter";
import ResignationDetailDrawer from "../components/ResignationDetailDrawer";
import { RESIGNATION_STATUS_FILTERS } from "../components/resignationFormat";
import {
  AppliedCell,
  LastDayCell,
  PersonCell,
  ReasonCell,
  ResignationCard,
  StatusCell,
} from "../components/resignationCells";
import { useResignation } from "../context/resignationContext";
import "../resignation.css";

/** Drawer filters the trigger badge counts. Status is not one of them: it has
 *  its own strip above the table. */
const DRAWER_FILTER_KEYS = ["department", "gender"];

const FilterTrigger = ({ count, open, onClick }) => (
  <button type="button" className="btn btn-outline-primary btn-sm" aria-haspopup="dialog" aria-expanded={open} onClick={onClick}>
    <i className="fa fa-filter" style={{ marginRight: 6 }} aria-hidden="true" />
    Filters
    {count > 0 && (
      <>
        <span className="filter-section__count ms-2" aria-hidden="true">{count}</span>
        <span className="visually-hidden">, {count} {count === 1 ? "filter" : "filters"} applied</span>
      </>
    )}
  </button>
);

const MobileCards = ({ loading, rows, pageSettings, onOpen, onLoadMore }) => {
  if (loading && !rows.length) return <Skeleton active avatar paragraph={{ rows: 4 }} />;
  if (!rows.length) return <div className="text-center text-muted py-5">No resignations match these filters.</div>;
  return (
    <>
      <div className="resignation-cards">
        {rows.map((record) => <ResignationCard key={record.id} record={record} onOpen={onOpen} />)}
      </div>
      <div className="my-3 d-flex justify-content-center">
        <Pagination
          pageSize={pageSettings?.pageSize}
          current={Number(pageSettings?.page)}
          total={pageSettings?.total}
          onChange={onLoadMore}
          showSizeChanger={false}
          hideOnSinglePage
          itemRender={(page, type) => (type === "next" ? <Button loading={loading} className="btn btn-primary" label="Load more" /> : null)}
        />
      </div>
    </>
  );
};

const AllResignations = () => {
  const {
    loading,
    setLoading,
    resignationData,
    view,
    pageSettings,
    handleGridChange,
    handleChange,
    handleFilterDrawer,
    showFilterDrawer,
    handleFilterChange,
    filters,
    handleInputChange,
    handleApplyFilter,
    filterLoading,
    handleDateChange,
    handleClearAll,
    resignationModal,
    setResignationModal,
    setSelectedResignation,
    selectedResignation,
    getResignationList,
    isFilterDirty,
  } = useResignation();
  const [searchParams] = useSearchParams();
  const [openRecord, setOpenRecord] = useState(null);
  const pinLeft = useStickyIdentity();

  const activeStatus = filters.find((item) => item.key === "status")?.value || "";
  const appliedFilterCount = DRAWER_FILTER_KEYS.filter((key) => searchParams.get(key)).length;

  const handleStatusChange = (key) => handleFilterChange({ label: key, value: key }, "status", true);

  const openApply = () => {
    setSelectedResignation("");
    setResignationModal(true);
  };

  const columns = [
    {
      title: "Employee",
      dataIndex: "first_name",
      sorter: true,
      ...pinLeft(260),
      render: (_, record) => <PersonCell record={record} onOpen={setOpenRecord} />,
    },
    {
      title: "Reason",
      dataIndex: "reason",
      width: 340,
      render: (reason) => <ReasonCell reason={reason} />,
    },
    {
      title: "Last working day",
      dataIndex: "last_working_day",
      sorter: true,
      width: 160,
      render: (_, record) => <LastDayCell record={record} />,
    },
    {
      title: "Status",
      dataIndex: "approval_status",
      sorter: true,
      width: 190,
      render: (_, record) => <StatusCell record={record} />,
    },
    {
      title: "Applied on",
      dataIndex: "created_at",
      sorter: true,
      width: 140,
      render: (value) => <AppliedCell value={value} />,
    },
  ];

  return (
    <>
      <div className="page-wrapper">
        <div className="content container-fluid">
          <PageHeader
            title="Resignations"
            subtitle="Review requests, reasons and where each one sits in the approval chain."
            actions={
              <>
                <FilterTrigger count={appliedFilterCount} open={showFilterDrawer} onClick={handleFilterDrawer} />
                <Button label="Apply Resignation" icon="fa fa-plus" className="btn btn-primary btn-sm" onClick={openApply} />
              </>
            }
          >
            <ResignationListFilter
              filters={filters}
              handleInputChange={handleInputChange}
              searchPlaceholder="Search name, emp no. or email"
              handleFilterDrawer={handleFilterDrawer}
              handleFilterChange={handleFilterChange}
              loading={loading}
            />
          </PageHeader>

          <ListShell
            filters={RESIGNATION_STATUS_FILTERS}
            activeKey={activeStatus}
            onFilterChange={handleStatusChange}
            resultCount={pageSettings?.total}
            loading={loading}
            className="resignation-list"
          >
            {view === "grid" ? (
              <div className="p-3">
                <MobileCards loading={loading} rows={resignationData} pageSettings={pageSettings} onOpen={setOpenRecord} onLoadMore={handleGridChange} />
              </div>
            ) : (
              <Table
                columns={columns}
                dataSource={resignationData}
                rowKey={(record) => record.id}
                loading={loading}
                scroll={{ x: "max-content" }}
                rowClassName="clickable-row"
                onRow={(record) => ({
                  onClick: (e) => {
                    if (e.target.closest("a, button")) return;
                    setOpenRecord(record);
                  },
                })}
                locale={{ emptyText: "No resignations match these filters." }}
                pagination={{
                  total: pageSettings?.total,
                  pageSize: Number(pageSettings?.pageSize),
                  current: Number(pageSettings?.page),
                  showSizeChanger: true,
                  pageSizeOptions,
                  itemRender,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                }}
                onChange={handleChange}
              />
            )}
          </ListShell>

          <AllResignationAddPopup
            getResignationList={getResignationList}
            setSelectedResignation={setSelectedResignation}
            setLoading={setLoading}
            selectedResignation={selectedResignation}
            loading={loading}
            resignationModal={resignationModal}
            setResignationModal={setResignationModal}
          />
        </div>
      </div>

      <ResignationDetailDrawer record={openRecord} onClose={() => setOpenRecord(null)} onChanged={getResignationList} />

      <ResignationFilterDrawer
        showFilterDrawer={showFilterDrawer}
        handleFilterDrawer={handleFilterDrawer}
        handleFilterChange={handleFilterChange}
        filters={filters}
        handleInputChange={handleInputChange}
        handleApplyFilter={handleApplyFilter}
        filterLoading={filterLoading}
        handleDateChange={handleDateChange}
        handleClearAll={handleClearAll}
        isFilterDirty={isFilterDirty}
      />
    </>
  );
};

export default AllResignations;
